import { Injectable } from '@angular/core';

/** Describes the contact currently being dragged from a customer table. */
export interface DraggedContactPayload {
  contactId: string;
  fullName: string;
}

/**
 * Shared state for the "drag a customer row onto the calendar" gesture.
 *
 * Why this exists instead of HTML5 `dataTransfer`: on Android System WebView the
 * native drag-and-drop strips custom MIME types. `dataTransfer.getData(
 * 'application/x-omni-contact')` returns empty both during `dragover` and at
 * `drop` — only `text/plain` survives the drag — so the contact id cannot ride
 * through `dataTransfer` cross-platform. Instead the drag source publishes the
 * payload here on `dragstart` and the drop target reads it on `drop`. The
 * payload auto-clears on `dragend`, which always fires on the source element
 * after the drop, so the drop handler still reads it in time.
 */
@Injectable({ providedIn: 'root' })
export class ContactDragStateService {
  private payload: DraggedContactPayload | null = null;
  private readonly clearOnDragEnd = (): void => this.endDrag();

  /** The contact currently being dragged, or null when no drag is in progress. */
  get draggedContact(): DraggedContactPayload | null {
    return this.payload;
  }

  /** Publish the dragged contact and arm an auto-clear when the drag ends. */
  beginDrag(payload: DraggedContactPayload): void {
    this.payload = payload;
    // Re-register a single capture-phase listener so a fresh drag never stacks
    // duplicates or leaks one from a previous drag.
    document.removeEventListener('dragend', this.clearOnDragEnd, true);
    document.addEventListener('dragend', this.clearOnDragEnd, true);
  }

  /** Clear the dragged contact. Normally invoked automatically on `dragend`. */
  endDrag(): void {
    this.payload = null;
    document.removeEventListener('dragend', this.clearOnDragEnd, true);
  }
}
